import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { shiftSchema } from "@src/appV2/Shifts/Shift/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError, isAxiosError } from "axios";
import { z } from "zod";

export enum ShiftStage {
  CLOCK_IN = "CLOCK_IN",
  /**
   * SKIP_LUNCH, LUNCH_OUT and LUNCH_IN are used by the HCP mobile app.
   * We've updated enum keys to be more precise and to avoid confusion.
   */
  SKIP_BREAK = "SKIP_LUNCH",
  BREAK_START = "LUNCH_OUT",
  BREAK_END = "LUNCH_IN",
  CLOCK_OUT = "CLOCK_OUT",
  SHIFT_TIME_DONE = "SHIFT_TIME_DONE",
}

export enum ActionCheckType {
  LOCATION = "LOCATION",
  NFC = "NFC",
  MANUAL = "MANUAL",
  INTEGRATION = "INTEGRATION",
}

const recordTimekeepingActionRequestSchema = z.object({
  shiftId: z.string(),
  stage: z.nativeEnum(ShiftStage),
  shiftActionCheck: z.nativeEnum(ActionCheckType).optional(),
  verificationMethod: z.nativeEnum(AttendanceVerificationMethod).optional(),

  complianceProof: z
    .object({
      bucketName: z.string(),
      fileKey: z.string(),
    })
    .optional(),

  appType: z.string().optional(),
  connectivityMode: z.string().optional(),
  location: z.number().array().optional(),
  locationType: z.string().optional(),
});

/**
 * The record_timekeeping_action endpoint returns a partial shift object
 * with updated timekeeping fields, not the complete shift.
 *
 * We should only add the fields we actually use to the response schema,
 * in order to avoid unnecessary coupling to the API return.
 *
 * WARNING: Using the full shiftSchema will cause validation errors because
 * the backend doesn't return all required fields (e.g., 'name' field is missing),
 * which breaks the clock in/out functionality.
 *
 * Fields returned by backend and used in the app:
 * - clockInOut, lunchInOut: Updated timekeeping fields (main purpose of endpoint)
 * - _id, start, end, agentReq, facility: Basic shift info for LiveActivity updates
 * - verified, timecard: For determining current shift stage in LiveActivity
 */
const recordTimekeepingActionResponseSchema = shiftSchema.pick({
  _id: true,
  start: true,
  end: true,
  agentReq: true,
  facility: true,
  clockInOut: true,
  lunchInOut: true,
  verified: true,
  timecard: true,
});

export type RecordTimekeepingActionRequest = z.infer<typeof recordTimekeepingActionRequestSchema>;

const timekeepingDataSchema = z.object({
  clockInOut: z
    .object({
      start: z.string().optional(),
      end: z.string().optional(),
    })
    .optional(),
  lunchInOut: z
    .object({
      start: z.string().optional(),
      end: z.string().optional(),
      isSkipped: z.boolean().optional(),
    })
    .optional(),
});

export type TimekeepingData = z.infer<typeof timekeepingDataSchema>;
export type RecordTimekeepingActionResponse = TimekeepingData;

export const DUPLICATE_ACTION_ERROR_MESSAGE =
  "Invalid state change. You can only record an action once.";

function normalizeTimekeepingData(data: {
  clockInOut?: { start?: string | null; end?: string | null } | null;
  lunchInOut?: {
    start?: string | null;
    end?: string | null;
    isSkipped?: boolean | null;
  } | null;
}): TimekeepingData {
  return {
    clockInOut: isDefined(data.clockInOut)
      ? {
          start: data.clockInOut.start ?? undefined,
          end: data.clockInOut.end ?? undefined,
        }
      : undefined,
    lunchInOut: isDefined(data.lunchInOut)
      ? {
          start: data.lunchInOut.start ?? undefined,
          end: data.lunchInOut.end ?? undefined,
          isSkipped: data.lunchInOut.isSkipped ?? undefined,
        }
      : undefined,
  };
}

/**
 * Checks whether the error response data confirms the requested action
 * was already performed. We only resolve as success if the specific
 * timestamp for the requested stage is present in the error data.
 */
function hasRequestedActionBeenRecorded(
  stage: ShiftStage,
  timekeepingData: TimekeepingData
): boolean {
  switch (stage) {
    case ShiftStage.CLOCK_IN: {
      return isDefined(timekeepingData.clockInOut?.start);
    }
    case ShiftStage.CLOCK_OUT: {
      return isDefined(timekeepingData.clockInOut?.end);
    }
    case ShiftStage.BREAK_START: {
      return isDefined(timekeepingData.lunchInOut?.start);
    }
    case ShiftStage.BREAK_END: {
      return isDefined(timekeepingData.lunchInOut?.end);
    }
    case ShiftStage.SKIP_BREAK: {
      return timekeepingData.lunchInOut?.isSkipped === true;
    }
    case ShiftStage.SHIFT_TIME_DONE: {
      return false;
    }
  }
}

export function getRecordTimekeepingActionPath(shiftId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/shifts/record_timekeeping_action/${shiftId}`;
}

export function useRecordShiftTimekeepingAction<T extends RecordTimekeepingActionRequest>(
  options: UseMutationOptions<TimekeepingData, AxiosError, T> = {}
): UseMutationResult<TimekeepingData, AxiosError, T> {
  return useMutation({
    mutationFn: async (data: T): Promise<TimekeepingData> => {
      const { shiftId, ...requestData } = data;

      try {
        const response = await post({
          url: getRecordTimekeepingActionPath(shiftId),
          data: requestData,
          requestSchema: recordTimekeepingActionRequestSchema.omit({ shiftId: true }),
          responseSchema: recordTimekeepingActionResponseSchema,
        });

        return normalizeTimekeepingData(response.data);
      } catch (error) {
        if (isAxiosError(error)) {
          const errorResponse = error.response?.data as {
            message: string;
            data?: Record<string, unknown>;
          };

          if (
            errorResponse?.message === DUPLICATE_ACTION_ERROR_MESSAGE &&
            isDefined(errorResponse.data)
          ) {
            const normalizedData = normalizeTimekeepingData(
              errorResponse.data as Parameters<typeof normalizeTimekeepingData>[0]
            );

            if (hasRequestedActionBeenRecorded(data.stage, normalizedData)) {
              return normalizedData;
            }
          }

          if (errorResponse?.message) {
            throw new Error(errorResponse.message);
          }
        }

        throw error;
      }
    },
    ...options,
  });
}
